import { Hand, ZoomIn, ZoomOut } from 'lucide-react';
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
} from 'react';
import type { Point } from '@obraplan/core/domain/takeoff/polygon';
import type { FrontPieceView, FrontRoomView } from '@obraplan/core/application/contracts/takeoff';
import type { PieceShape } from '@obraplan/core/domain/takeoff/piece-shape';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import {
  ampliar,
  ancorar,
  aptosDaFrente,
  ancoraDaForma,
  escalaNaTela,
  limitar,
  naTela,
  type Enquadramento as Quadro,
  aptosDaPlanta,
  centroDoContorno,
  chaveDaPeca,
  chaveDoComodo,
  dentroDoRetangulo,
  enquadrar,
  estadoDaPeca,
  estadoDoComodo,
  LEGENDA_DO_COMODO,
  nomeDoApto,
  percentual,
  tipologiasRepetidas,
  type EstadoDaPeca,
  type EstadoDoComodo,
} from './planta-pintada';
import type { FundoDaPlanta } from './use-prancha-da-frente';

/**
 * A planta do pavimento pintada para um serviço (Fase 34, etapa 2 — RN-448, D3, D14).
 *
 * Os cômodos de cada apto, pelo contorno do desenho, na cor do percentual que vale: cinza não
 * começado, amarelo em andamento, verde concluído e riscado o "não se aplica". A peça da
 * estrutura (estaca, bloco…) é pintada pela marca, onde está na prancha (RN-446).
 *
 * Tudo nas coordenadas da prancha do pavimento, em metros, com o y para baixo — o mesmo da
 * bancada. A tipologia repetida em dois aptos tem um contorno só: pinta-se um apto de cada vez, e
 * a escolha fica acima da planta.
 */

const COR_DO_COMODO: Readonly<Record<EstadoDoComodo, string>> = {
  NAO_INICIADO: 'var(--color-state-neutral)',
  EM_ANDAMENTO: 'var(--color-state-warn)',
  CONCLUIDO: 'var(--color-state-ok)',
  NAO_SE_APLICA: 'var(--color-state-neutral)',
};

const COR_DA_PECA: Readonly<Record<EstadoDaPeca, string>> = {
  NAO_INICIADA: 'var(--color-state-neutral)',
  EXECUTADA: 'var(--color-state-ok)',
};

/** A tinta do preenchimento: a cor do estado, clara o bastante para o texto por cima. */
function tinta(cor: string, forca = 30): string {
  return `color-mix(in oklch, ${cor} ${String(forca)}%, transparent)`;
}

/**
 * Acima disto **à vista**, a peça não leva o nome escrito na planta — só na dica, ao passar o
 * mouse. Na Fundação inteira da Veneza (107 estacas) os nomes se atropelavam; com zoom, eles
 * voltam (23/10/2026).
 */
const NOMES_ATE = 60;

export interface PlantaPintadaProps {
  readonly rooms?: readonly FrontRoomView[];
  readonly pieces?: readonly FrontPieceView[];
  /** "Piso cerâmico · 5º Pavimento" — o que a planta mostra, para quem não a vê. */
  readonly label: string;
  readonly className?: string;
  /** O tamanho do desenho: no painel, até 28rem de altura; na tela cheia, o que sobrar. */
  readonly svgClassName?: string;
  /** Com estes, cada cômodo e cada peça viram botão — a marcação na tela cheia. */
  readonly onSelectRoom?: (room: FrontRoomView) => void;
  readonly onSelectPiece?: (piece: FrontPieceView) => void;
  /** Os escolhidos, contornados: `chaveDoComodo` do cômodo, ou `chaveDaPeca`. */
  readonly selected?: ReadonlySet<string> | undefined;
  /**
   * Com este, arrastar sobre o fundo da planta desenha um retângulo, e o que tem o centro dentro
   * dele é escolhido de uma vez: as chaves vão para quem chamou.
   */
  readonly onSelectArea?: (keys: readonly string[]) => void;
  /**
   * A prancha do desenho, por baixo de tudo, em metros a partir do (0, 0) da página — a mesma
   * conta da bancada (`usePranchaDaFrente`). Ela não recebe clique: o arrasto e o clique
   * continuam dos cômodos e das peças.
   */
  readonly fundo?: FundoDaPlanta | undefined;
}

/** Enter e espaço clicam, como num botão. */
function comoBotao(acao: () => void) {
  return {
    role: 'button',
    tabIndex: 0,
    className: 'cursor-pointer outline-none focus-visible:opacity-80',
    onClick: acao,
    onKeyDown: (event: KeyboardEvent<SVGGElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        acao();
      }
    },
  } as const;
}

export function PlantaPintada({
  rooms = [],
  pieces = [],
  label,
  className,
  svgClassName = 'max-h-[28rem] w-full',
  onSelectRoom,
  onSelectPiece,
  selected,
  onSelectArea,
  fundo,
}: PlantaPintadaProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [arrasto, setArrasto] = useState<{ a: Point; b: Point } | undefined>(undefined);
  /** O pedaço da prancha à vista; ausente, o quadro inteiro ("Ajustar"). */
  const [vista, setVista] = useState<Quadro | undefined>(undefined);
  /** Com o "Mover" ligado, arrastar desloca a planta em vez de escolher por área. */
  const [movendo, setMovendo] = useState(false);
  /** Os dedos e o mouse na planta, pela posição na tela — o gesto de mover e o de pinça. */
  const ponteiros = useRef(new Map<number, { x: number; y: number }>());
  const gesto = useRef<
    | {
        readonly kind: 'mover';
        readonly inicio: { x: number; y: number };
        readonly vista: Quadro;
      }
    | {
        readonly kind: 'pinca';
        readonly distancia: number;
        readonly ancora: Point;
        readonly vista: Quadro;
      }
    | undefined
  >(undefined);
  const hachura = useId().replaceAll(':', '');
  const aptos = useMemo(() => aptosDaFrente(rooms), [rooms]);
  const repetidas = useMemo(() => tipologiasRepetidas(aptos), [aptos]);
  const [escolhidos, setEscolhidos] = useState<Record<string, string>>({});

  const pintados = aptosDaPlanta(aptos, escolhidos);
  const comodos = pintados.flatMap((apto) =>
    apto.rooms.flatMap((room) =>
      room.outline === undefined ? [] : [{ room, outline: room.outline }],
    ),
  );
  const pecas = pieces.flatMap((piece) =>
    piece.shape === undefined ? [] : [{ piece, shape: piece.shape }],
  );
  // O quadro é o do que se marca, com folga; a prancha aparece em volta. Sem nada no lugar, a
  // prancha inteira — para ao menos ver o pavimento.
  const quadro =
    enquadrar(
      comodos.map((item) => item.outline),
      pecas.map((item) => item.shape),
    ) ??
    (fundo === undefined ? undefined : { x: 0, y: 0, width: fundo.width, height: fundo.height });
  const semTraco = pintados.reduce(
    (soma, apto) => soma + apto.rooms.filter((room) => room.outline === undefined).length,
    0,
  );
  const semLugar = pieces.length - pecas.length;
  const atual = vista ?? quadro;

  // Ctrl + roda amplia no ponto do mouse, como na bancada; a roda sozinha, com a planta
  // ampliada, a desloca. O ouvinte é do elemento, e não do React: o do React é passivo, e a
  // página rolaria junto.
  const paraARoda = useRef({ atual, quadro, fundo, vista });
  paraARoda.current = { atual, quadro, fundo, vista };
  const temPlanta = quadro !== undefined;
  useEffect(() => {
    const svg = svgRef.current;
    if (svg === null || !temPlanta) return undefined;
    const aoRolar = (event: WheelEvent) => {
      const { atual, quadro, fundo, vista } = paraARoda.current;
      if (atual === undefined || quadro === undefined) return;
      const caixa = svg.getBoundingClientRect();
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
        const fator = Math.exp(event.deltaY * 0.0015);
        setVista(
          limitar(
            ampliar(atual, fator, { x: event.clientX, y: event.clientY }, caixa),
            quadro,
            fundo,
          ),
        );
        return;
      }
      if (vista === undefined) return;
      event.preventDefault();
      const escala = escalaNaTela(atual, caixa);
      setVista(
        limitar(
          {
            ...atual,
            x: atual.x + (event.shiftKey ? event.deltaY : event.deltaX) / escala,
            y: atual.y + (event.shiftKey ? 0 : event.deltaY) / escala,
          },
          quadro,
          fundo,
        ),
      );
    };
    svg.addEventListener('wheel', aoRolar, { passive: false });
    return () => svg.removeEventListener('wheel', aoRolar);
  }, [temPlanta]);

  if (quadro === undefined || atual === undefined) {
    return (
      <p className={cn('text-sm text-(--color-content-muted)', className)}>
        {rooms.length === 0 && pieces.length === 0
          ? 'Nada para desenhar neste pavimento.'
          : 'O desenho deste pavimento não tem contorno para pintar: a lista abaixo mostra o andamento.'}
      </p>
    );
  }

  // O tamanho das letras, dos pontos e dos anéis sai do pedaço à vista, e não do quadro: com
  // zoom, eles ficam do mesmo tamanho na tela e param de se encavalar.
  const lado = Math.max(atual.width, atual.height);
  const letra = lado / 48;
  const estados = new Set(comodos.map((item) => estadoDoComodo(item.room)));
  const estadosDasPecas = new Set(pecas.map((item) => estadoDaPeca(item.piece)));
  // O nome só vai escrito quando cabe: até `NOMES_ATE` peças à vista. Fora disso, a dica.
  const aVista = pecas.filter((item) =>
    dentroDoRetangulo(
      ancoraDaForma(item.shape),
      { x: atual.x, y: atual.y },
      { x: atual.x + atual.width, y: atual.y + atual.height },
    ),
  ).length;
  const ampliada = vista !== undefined;

  /** O ponto do evento nas coordenadas da prancha, pelo inverso da matriz da tela. */
  const naPrancha = (event: PointerEvent<SVGSVGElement>): Point | undefined => {
    const matriz = svgRef.current?.getScreenCTM();
    if (matriz === null || matriz === undefined) return undefined;
    const p = new DOMPoint(event.clientX, event.clientY).matrixTransform(matriz.inverse());
    return { x: p.x, y: p.y };
  };
  const caixa = (): DOMRect | undefined => svgRef.current?.getBoundingClientRect();
  const zoom = (fator: number): void => {
    const medida = caixa();
    // Sem medida (ainda fora da tela), uma caixa da proporção da vista: o meio é o mesmo.
    const c =
      medida !== undefined && medida.width > 0 && medida.height > 0
        ? medida
        : { left: 0, top: 0, width: 1000, height: (1000 * atual.height) / atual.width };
    setVista(
      limitar(
        ampliar(atual, fator, { x: c.left + c.width / 2, y: c.top + c.height / 2 }, c),
        quadro,
        fundo,
      ),
    );
  };

  const gestos = {
    onPointerDown: (event: PointerEvent<SVGSVGElement>) => {
      ponteiros.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
      const c = caixa();
      // Dois dedos: a pinça amplia e desloca, e cancela a escolha por área.
      if (ponteiros.current.size === 2 && c !== undefined) {
        const [p1, p2] = [...ponteiros.current.values()] as [Point, Point];
        const meio = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
        gesto.current = {
          kind: 'pinca',
          distancia: Math.max(1, Math.hypot(p1.x - p2.x, p1.y - p2.y)),
          ancora: naTela(atual, meio, c),
          vista: atual,
        };
        setArrasto(undefined);
        event.currentTarget.setPointerCapture(event.pointerId);
        return;
      }
      const naFigura = (event.target as Element).closest('[role="button"]') !== null;
      // Mover: com o botão ligado (também começando sobre uma peça), com o botão do meio, ou
      // onde não há escolha por área. Fora disso, o clique numa figura é dela.
      if (movendo || event.button === 1 || (onSelectArea === undefined && !naFigura)) {
        if (onSelectArea === undefined && !ampliada && !movendo) return;
        gesto.current = {
          kind: 'mover',
          inicio: { x: event.clientX, y: event.clientY },
          vista: atual,
        };
        event.currentTarget.setPointerCapture(event.pointerId);
        return;
      }
      if (naFigura) return;
      const ponto = naPrancha(event);
      if (ponto === undefined) return;
      event.currentTarget.setPointerCapture(event.pointerId);
      setArrasto({ a: ponto, b: ponto });
    },
    onPointerMove: (event: PointerEvent<SVGSVGElement>) => {
      if (ponteiros.current.has(event.pointerId)) {
        ponteiros.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
      }
      const agora = gesto.current;
      const c = caixa();
      if (agora?.kind === 'pinca' && ponteiros.current.size >= 2 && c !== undefined) {
        const [p1, p2] = [...ponteiros.current.values()] as [Point, Point];
        const meio = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
        const fator = agora.distancia / Math.max(1, Math.hypot(p1.x - p2.x, p1.y - p2.y));
        const largura = agora.vista.width * fator;
        const altura = agora.vista.height * fator;
        setVista(
          limitar(
            ancorar({ width: largura, height: altura }, agora.ancora, meio, c),
            quadro,
            fundo,
          ),
        );
        return;
      }
      if (agora?.kind === 'mover' && c !== undefined) {
        const escala = escalaNaTela(agora.vista, c);
        setVista(
          limitar(
            {
              ...agora.vista,
              x: agora.vista.x - (event.clientX - agora.inicio.x) / escala,
              y: agora.vista.y - (event.clientY - agora.inicio.y) / escala,
            },
            quadro,
            fundo,
          ),
        );
        return;
      }
      if (arrasto === undefined) return;
      const ponto = naPrancha(event);
      if (ponto !== undefined) setArrasto({ a: arrasto.a, b: ponto });
    },
    onPointerUp: (event: PointerEvent<SVGSVGElement>) => {
      ponteiros.current.delete(event.pointerId);
      if (gesto.current !== undefined) {
        if (ponteiros.current.size === 0) gesto.current = undefined;
        return;
      }
      if (arrasto === undefined || onSelectArea === undefined) return;
      const { a, b } = arrasto;
      setArrasto(undefined);
      // Um toque sem arrastar não escolhe nada.
      if (Math.abs(a.x - b.x) < lado / 200 && Math.abs(a.y - b.y) < lado / 200) return;
      onSelectArea([
        ...comodos
          .filter((item) => dentroDoRetangulo(centroDoContorno(item.outline), a, b))
          .map((item) => chaveDoComodo(item.room)),
        ...pecas
          .filter((item) => dentroDoRetangulo(ancoraDaForma(item.shape), a, b))
          .map((item) => chaveDaPeca(item.piece)),
      ]);
    },
    onPointerCancel: (event: PointerEvent<SVGSVGElement>) => {
      ponteiros.current.delete(event.pointerId);
      if (ponteiros.current.size === 0) gesto.current = undefined;
      setArrasto(undefined);
    },
  };

  return (
    <figure className={cn('flex flex-col gap-2', className)}>
      {repetidas.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
          {repetidas.map(({ unitTypeId, aptos: daTipologia }) => {
            const atual = pintados.find((apto) => apto.unitTypeId === unitTypeId)?.positionId;
            return (
              <span key={unitTypeId} className="inline-flex flex-wrap items-center gap-1">
                <span className="text-(--color-content-muted)">
                  {daTipologia[0]!.unitTypeName}: pintando o
                </span>
                {daTipologia.map((apto) => (
                  <button
                    key={apto.positionId}
                    type="button"
                    aria-pressed={apto.positionId === atual}
                    className={cn(
                      'rounded-full border border-(--color-border-subtle) px-2 py-0.5',
                      apto.positionId === atual &&
                        'border-(--color-brand) bg-(--color-brand-soft) font-medium',
                    )}
                    onClick={() =>
                      setEscolhidos((antes) => ({ ...antes, [unitTypeId]: apto.positionId }))
                    }
                  >
                    {nomeDoApto(apto.label)}
                  </button>
                ))}
              </span>
            );
          })}
        </div>
      )}

      <div
        role="toolbar"
        aria-label="Zoom da planta"
        className="flex flex-wrap items-center justify-end gap-1.5 text-xs"
      >
        <span className="mr-auto text-(--color-content-muted)">
          {onSelectArea === undefined
            ? 'Ctrl + roda ou pinça para ampliar; arraste para mover.'
            : 'Ctrl + roda ou pinça para ampliar; "Mover" para arrastar a planta.'}
        </span>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          aria-label="Ampliar"
          onClick={() => zoom(1 / 1.6)}
        >
          <ZoomIn aria-hidden="true" />
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          aria-label="Reduzir"
          onClick={() => zoom(1.6)}
        >
          <ZoomOut aria-hidden="true" />
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={!ampliada}
          onClick={() => setVista(undefined)}
        >
          Ajustar
        </Button>
        {onSelectArea !== undefined && (
          <Button
            type="button"
            variant={movendo ? 'primary' : 'secondary'}
            size="sm"
            aria-pressed={movendo}
            onClick={() => setMovendo((antes) => !antes)}
          >
            <Hand aria-hidden="true" />
            Mover
          </Button>
        )}
      </div>

      <svg
        ref={svgRef}
        {...gestos}
        role="img"
        aria-label={`Planta pintada: ${label}`}
        viewBox={`${String(atual.x)} ${String(atual.y)} ${String(atual.width)} ${String(atual.height)}`}
        className={cn(
          'rounded-md border border-(--color-border-subtle) bg-(--color-surface-sunken)',
          svgClassName,
          (onSelectArea !== undefined || ampliada) && 'touch-none select-none',
          (movendo || (onSelectArea === undefined && ampliada)) && 'cursor-grab',
        )}
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <pattern
            id={hachura}
            patternUnits="userSpaceOnUse"
            width={lado / 60}
            height={lado / 60}
            patternTransform="rotate(45)"
          >
            <line
              x1="0"
              y1="0"
              x2="0"
              y2={lado / 60}
              stroke="var(--color-state-neutral)"
              strokeWidth={lado / 240}
            />
          </pattern>
        </defs>

        {fundo !== undefined && (
          <image
            href={fundo.image}
            x={0}
            y={0}
            width={fundo.width}
            height={fundo.height}
            preserveAspectRatio="none"
            pointerEvents="none"
            data-part="prancha"
          />
        )}

        {comodos.map(({ room, outline }) => {
          const estado = estadoDoComodo(room);
          const centro = centroDoContorno(outline);
          const pontos = outline.map((p) => `${String(p.x)},${String(p.y)}`).join(' ');
          const texto =
            estado === 'NAO_SE_APLICA'
              ? 'não se aplica'
              : room.percent === undefined
                ? '—'
                : percentual(room.percent);
          const nome = `${room.roomName} · ${nomeDoApto(room.positionLabel)}`;
          const escolhido = selected?.has(chaveDoComodo(room)) === true;
          return (
            <g
              key={chaveDoComodo(room)}
              data-estado={estado}
              {...(onSelectRoom === undefined
                ? {}
                : { ...comoBotao(() => onSelectRoom(room)), 'aria-label': `${nome} — ${texto}` })}
            >
              <title>{`${nome} — ${texto}`}</title>
              <polygon
                points={pontos}
                fill={
                  estado === 'NAO_SE_APLICA' ? `url(#${hachura})` : tinta(COR_DO_COMODO[estado])
                }
                stroke={escolhido ? 'var(--color-brand)' : 'var(--color-content-muted)'}
                strokeWidth={escolhido ? 3 : 1}
                vectorEffect="non-scaling-stroke"
              />
              <text
                x={centro.x}
                y={centro.y}
                textAnchor="middle"
                fontSize={letra}
                fill="var(--color-content)"
              >
                <tspan x={centro.x} dy={-letra * 0.2}>
                  {room.roomName}
                </tspan>
                <tspan x={centro.x} dy={letra * 1.15} fontWeight={600}>
                  {texto}
                </tspan>
              </text>
            </g>
          );
        })}

        {pecas.map(({ piece, shape }) => (
          <Peca
            key={piece.name}
            piece={piece}
            shape={shape}
            lado={lado}
            comNome={aVista <= NOMES_ATE}
            escolhida={selected?.has(chaveDaPeca(piece)) === true}
            {...(onSelectPiece === undefined ? {} : { onSelect: () => onSelectPiece(piece) })}
          />
        ))}

        {arrasto !== undefined && (
          <rect
            x={Math.min(arrasto.a.x, arrasto.b.x)}
            y={Math.min(arrasto.a.y, arrasto.b.y)}
            width={Math.abs(arrasto.a.x - arrasto.b.x)}
            height={Math.abs(arrasto.a.y - arrasto.b.y)}
            fill="color-mix(in oklch, var(--color-brand) 12%, transparent)"
            stroke="var(--color-brand)"
            strokeDasharray="4 3"
            strokeWidth={1}
            vectorEffect="non-scaling-stroke"
            pointerEvents="none"
          />
        )}
      </svg>

      <figcaption className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-(--color-content-muted)">
        {(['NAO_INICIADO', 'EM_ANDAMENTO', 'CONCLUIDO', 'NAO_SE_APLICA'] as const)
          .filter((estado) => estados.has(estado))
          .map((estado) => (
            <span key={estado} className="inline-flex items-center gap-1.5">
              <svg aria-hidden="true" viewBox="0 0 12 12" className="size-3">
                <rect
                  width="12"
                  height="12"
                  fill={
                    estado === 'NAO_SE_APLICA'
                      ? `url(#${hachura})`
                      : tinta(COR_DO_COMODO[estado], 45)
                  }
                  stroke="var(--color-content-muted)"
                />
              </svg>
              {LEGENDA_DO_COMODO[estado]}
            </span>
          ))}
        {(['NAO_INICIADA', 'EXECUTADA'] as const)
          .filter((estado) => estadosDasPecas.has(estado))
          .map((estado) => (
            <span key={estado} className="inline-flex items-center gap-1.5">
              <span
                aria-hidden="true"
                className="inline-block size-3 rounded-full"
                style={{ background: COR_DA_PECA[estado] }}
              />
              {estado === 'EXECUTADA' ? 'Peça executada' : 'Peça não executada'}
            </span>
          ))}
        {semTraco > 0 && (
          <span>
            {semTraco === 1
              ? '1 cômodo sem contorno no desenho fica só na lista.'
              : `${String(semTraco)} cômodos sem contorno no desenho ficam só na lista.`}
          </span>
        )}
        {semLugar > 0 && (
          <span>
            {semLugar === 1
              ? '1 peça sem lugar na planta fica só na lista.'
              : `${String(semLugar)} peças sem lugar na planta ficam só na lista.`}
          </span>
        )}
      </figcaption>
    </figure>
  );
}

function Peca({
  piece,
  shape,
  lado,
  comNome,
  escolhida,
  onSelect,
}: {
  readonly piece: FrontPieceView;
  readonly shape: PieceShape;
  readonly lado: number;
  readonly comNome: boolean;
  readonly escolhida: boolean;
  readonly onSelect?: () => void;
}) {
  const estado = estadoDaPeca(piece);
  const cor = COR_DA_PECA[estado];
  const dica = `${piece.name} — ${estado === 'EXECUTADA' ? `executada em ${piece.executedOn!.split('-').reverse().join('/')}` : 'não executada'}`;
  let letra = lado / 70;

  let figura: ReactNode;
  let rotulo: { x: number; y: number } | undefined;
  switch (shape.kind) {
    case 'CIRCULO': {
      const raio = Math.max(shape.radius, lado / 300);
      figura = (
        <circle
          cx={shape.center.x}
          cy={shape.center.y}
          r={raio}
          fill={tinta(cor, 70)}
          stroke={cor}
          strokeWidth={1}
          vectorEffect="non-scaling-stroke"
        />
      );
      // O nome dentro da estaca, do tamanho dela: "E12" lido sem sair do círculo.
      letra = Math.min(raio * 0.7, lado / 30);
      rotulo = { x: shape.center.x, y: shape.center.y + letra * 0.35 };
      break;
    }
    case 'PONTO':
      figura = (
        <circle
          cx={shape.at.x}
          cy={shape.at.y}
          r={lado / 110}
          fill={tinta(cor, 80)}
          stroke={cor}
          strokeWidth={1}
          vectorEffect="non-scaling-stroke"
        />
      );
      rotulo = { x: shape.at.x, y: shape.at.y - (lado / 110) * 1.6 };
      break;
    case 'POLIGONOS':
      figura = shape.outlines.map((contorno, indice) => (
        <polygon
          key={indice}
          points={contorno.map((p) => `${String(p.x)},${String(p.y)}`).join(' ')}
          fill={tinta(cor, 45)}
          stroke={cor}
          strokeWidth={1.5}
          vectorEffect="non-scaling-stroke"
        />
      ));
      rotulo = shape.outlines[0] === undefined ? undefined : centroDoContorno(shape.outlines[0]);
      break;
    case 'LINHA':
      figura = (
        <line
          x1={shape.from.x}
          y1={shape.from.y}
          x2={shape.to.x}
          y2={shape.to.y}
          stroke={tinta(cor, 75)}
          strokeWidth={Math.max(shape.width, lado / 400)}
        />
      );
      rotulo = { x: (shape.from.x + shape.to.x) / 2, y: (shape.from.y + shape.to.y) / 2 };
      break;
  }

  // A escolhida ganha o contorno da marca, por fora da figura.
  const anel = lado / 90;
  const destaque = !escolhida ? null : shape.kind === 'CIRCULO' || shape.kind === 'PONTO' ? (
    <circle
      cx={shape.kind === 'CIRCULO' ? shape.center.x : shape.at.x}
      cy={shape.kind === 'CIRCULO' ? shape.center.y : shape.at.y}
      r={(shape.kind === 'CIRCULO' ? Math.max(shape.radius, lado / 300) : lado / 110) + anel}
      fill="none"
      stroke="var(--color-brand)"
      strokeWidth={3}
      vectorEffect="non-scaling-stroke"
    />
  ) : shape.kind === 'POLIGONOS' ? (
    shape.outlines.map((contorno, indice) => (
      <polygon
        key={indice}
        points={contorno.map((p) => `${String(p.x)},${String(p.y)}`).join(' ')}
        fill="none"
        stroke="var(--color-brand)"
        strokeWidth={3}
        vectorEffect="non-scaling-stroke"
      />
    ))
  ) : (
    <line
      x1={shape.from.x}
      y1={shape.from.y}
      x2={shape.to.x}
      y2={shape.to.y}
      stroke="var(--color-brand)"
      strokeWidth={3}
      vectorEffect="non-scaling-stroke"
    />
  );

  return (
    <g
      data-estado={estado}
      data-peca={piece.name}
      {...(onSelect === undefined ? {} : { ...comoBotao(onSelect), 'aria-label': dica })}
    >
      <title>{dica}</title>
      {figura}
      {destaque}
      {comNome && rotulo !== undefined && (
        <text
          x={rotulo.x}
          y={rotulo.y}
          textAnchor="middle"
          fontSize={letra}
          fill="var(--color-content)"
        >
          {piece.name}
        </text>
      )}
    </g>
  );
}
